import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { 
  X, 
  Building, 
  Calendar, 
  DollarSign, 
  MapPin, 
  Clock, 
  FileText,
  Globe,
  User,
  Download,
  Loader2,
  AlertCircle,
  CheckCircle
} from 'lucide-react'
import apiService from '../../services/api'
import { useAuthStore } from '../../store/authStore'

interface RfqDetailModalProps {
  rfqId: string
  isOpen: boolean
  onClose: () => void
}

interface DetailedRfq {
  id: string
  title: string
  description: string
  budgetRange: string
  location: string
  deadline: string
  attachments: string[]
  createdAt: string
  updatedAt: string
  GovAgency: {
    id: string
    name: string
    agencyType: string
    website?: string
    contactPerson: string
    email: string
  }
}

export default function RfqDetailModal({ rfqId, isOpen, onClose }: RfqDetailModalProps) {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  
  const { data: rfq, isLoading, error } = useQuery<DetailedRfq>({
    queryKey: ['rfq-detail', rfqId],
    queryFn: async () => {
      const response = await apiService.get(`/api/gov/rfqs/public/${rfqId}`)
      if (typeof response === 'object' && response && 'data' in response) {
        return (response as any).data;
      }
      return undefined;
    },
    enabled: isOpen && !!rfqId,
    staleTime: 5 * 60 * 1000
  })

  if (!isOpen) return null

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getDaysUntilDeadline = (deadline: string) => {
    const now = new Date()
    const deadlineDate = new Date(deadline)
    const diffTime = deadlineDate.getTime() - now.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return diffDays
  }

  // Support both new standardized (VENDOR + isEnterprise=true) and legacy (ENTERPRISE_VENDOR) roles
  const isEnterpriseVendor = (user?.role === 'VENDOR' && user?.isEnterprise === true) || user?.role === 'ENTERPRISE_VENDOR';
  const canRespond = isEnterpriseVendor && user?.isGovCertified === true

  const handleSubmitProposal = () => {
    onClose()
    navigate(`/gov/rfqs/${rfqId}/respond`)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">RFQ Details</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <Loader2 className="h-8 w-8 animate-spin text-emerald-600 mx-auto mb-4" />
                <p className="text-gray-600">Loading RFQ details...</p>
              </div>
            </div>
          )}

          {error && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Unable to Load RFQ</h3>
                <p className="text-gray-600">
                  We're having trouble loading this RFQ. Please try again later.
                </p>
              </div>
            </div>
          )}

          {rfq && (
            <div className="space-y-6">
              {/* Title and Agency */}
              <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">{rfq.title}</h1>
                <div className="flex items-center text-gray-600 mb-4">
                  <Building className="h-5 w-5 mr-2" />
                  <span className="text-lg">{rfq.GovAgency.name}</span>
                </div>
                <div className="flex items-center space-x-4 text-sm">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    RFQ-{rfq.id.slice(-8).toUpperCase()}
                  </span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    <FileText className="h-3 w-3 mr-1" />
                    {rfq.GovAgency.agencyType}
                  </span>
                </div>
              </div>

              {/* Key Information Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="flex items-center mb-2">
                    <DollarSign className="h-5 w-5 text-green-600 mr-2" />
                    <h3 className="font-medium text-green-900">Budget Range</h3>
                  </div>
                  <p className="text-xl font-bold text-green-900">{rfq.budgetRange}</p>
                </div>

                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="flex items-center mb-2">
                    <MapPin className="h-5 w-5 text-blue-600 mr-2" />
                    <h3 className="font-medium text-blue-900">Location</h3>
                  </div>
                  <p className="text-lg font-semibold text-blue-900">{rfq.location}</p>
                </div>

                <div className="bg-purple-50 p-4 rounded-lg">
                  <div className="flex items-center mb-2">
                    <Calendar className="h-5 w-5 text-purple-600 mr-2" />
                    <h3 className="font-medium text-purple-900">Posted</h3>
                  </div>
                  <p className="text-sm font-semibold text-purple-900">
                    {formatDate(rfq.createdAt)}
                  </p>
                  <p className="text-xs text-purple-700">
                    {formatTime(rfq.createdAt)}
                  </p>
                </div>

                <div className={`p-4 rounded-lg ${
                  getDaysUntilDeadline(rfq.deadline) <= 7 
                    ? 'bg-red-50' 
                    : 'bg-orange-50'
                }`}>
                  <div className="flex items-center mb-2">
                    <Clock className={`h-5 w-5 mr-2 ${
                      getDaysUntilDeadline(rfq.deadline) <= 7 
                        ? 'text-red-600' 
                        : 'text-orange-600'
                    }`} />
                    <h3 className={`font-medium ${
                      getDaysUntilDeadline(rfq.deadline) <= 7 
                        ? 'text-red-900' 
                        : 'text-orange-900'
                    }`}>
                      Response Deadline
                    </h3>
                  </div>
                  <p className={`text-sm font-semibold ${
                    getDaysUntilDeadline(rfq.deadline) <= 7 
                      ? 'text-red-900' 
                      : 'text-orange-900'
                  }`}>
                    {formatDate(rfq.deadline)}
                  </p>
                  <p className={`text-xs ${
                    getDaysUntilDeadline(rfq.deadline) <= 7 
                      ? 'text-red-700' 
                      : 'text-orange-700'
                  }`}>
                    {getDaysUntilDeadline(rfq.deadline) > 0 
                      ? `${getDaysUntilDeadline(rfq.deadline)} days left`
                      : 'Deadline passed'
                    }
                  </p>
                </div>
              </div>

              {/* Description */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Project Description</h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                    {rfq.description}
                  </p>
                </div>
              </div>

              {/* Agency Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Agency Information</h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div className="flex items-center mb-2">
                        <Building className="h-4 w-4 text-gray-600 mr-2" />
                        <span className="text-sm font-medium text-gray-900">Agency</span>
                      </div>
                      <p className="text-gray-700">{rfq.GovAgency.name}</p>
                    </div>
                    
                    <div>
                      <div className="flex items-center mb-2">
                        <User className="h-4 w-4 text-gray-600 mr-2" />
                        <span className="text-sm font-medium text-gray-900">Contact Person</span>
                      </div>
                      <p className="text-gray-700">{rfq.GovAgency.contactPerson}</p>
                    </div>

                    {rfq.GovAgency.website && (
                      <div>
                        <div className="flex items-center mb-2">
                          <Globe className="h-4 w-4 text-gray-600 mr-2" />
                          <span className="text-sm font-medium text-gray-900">Website</span>
                        </div>
                        <a 
                          href={rfq.GovAgency.website} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 underline"
                        >
                          {rfq.GovAgency.website}
                        </a>
                      </div>
                    )}

                    <div>
                      <div className="flex items-center mb-2">
                        <FileText className="h-4 w-4 text-gray-600 mr-2" />
                        <span className="text-sm font-medium text-gray-900">Agency Type</span>
                      </div>
                      <p className="text-gray-700">{rfq.GovAgency.agencyType}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Attachments */}
              {rfq.attachments && rfq.attachments.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Attachments</h3>
                  <div className="space-y-2">
                    {rfq.attachments.map((attachment, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center">
                          <FileText className="h-5 w-5 text-gray-600 mr-3" />
                          <span className="text-gray-900">{attachment}</span>
                        </div>
                        <button className="flex items-center text-sm text-blue-600 hover:text-blue-800 transition-colors">
                          <Download className="h-4 w-4 mr-1" />
                          Download
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Response Status */}
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <CheckCircle className="h-5 w-5 text-emerald-600 mr-2" />
                  <h3 className="font-medium text-emerald-900">Response Status</h3>
                </div>
                {!user ? (
                  <p className="text-emerald-700 text-sm mb-3">
                    Please log in to respond to this RFQ.
                  </p>
                ) : !isEnterpriseVendor ? (
                  <p className="text-emerald-700 text-sm mb-3">
                    Only enterprise vendors can respond to government RFQs.
                  </p>
                ) : !user?.isGovCertified ? (
                  <div className="text-emerald-700 text-sm mb-3">
                    <p className="mb-2">
                      ⚠️ Government certification required to submit proposals to government RFQs.
                    </p>
                    <p className="text-xs">
                      You can view this RFQ but cannot submit a proposal without government certification.
                  </p>
                  </div>
                ) : (
                  <p className="text-emerald-700 text-sm mb-3">
                    ✅ You can submit a proposal for this RFQ.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        {rfq && (
          <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">
                Last updated: {formatDate(rfq.updatedAt)}
              </span>
            </div>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
              
              {canRespond ? (
                <button
                  onClick={handleSubmitProposal}
                  className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
                >
                  Submit Proposal
                </button>
              ) : (
                <button
                  disabled
                  className="px-6 py-2 bg-gray-300 text-gray-500 rounded-md font-medium cursor-not-allowed"
                  title={
                    !user 
                      ? "Please log in to respond"
                      : !isEnterpriseVendor 
                        ? "Enterprise vendor account required"
                        : "Government certification required"
                  }
                >
                  Submit Proposal
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
} 